/* eslint-disable @typescript-eslint/no-misused-promises */
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  SmartAutoComplete,
  SmartDateTimePicker,
  SmartForm,
  SmartFormProps,
  SmartTextField,
  usePopup,
  useSmartForm,
} from "@nodeon/react-components";
import React from "react";
import { If } from "react-extras";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  DesignMaterialUpdateDto,
  ReturnNodeDto,
  useDesignMaterialApi,
  useMapAreaApi,
} from "~/api/GeneratedApi";
import FileExplorer from "~/components/file-explorer/FileExplorer";
import { FileImportDialog } from "~/components/FileImportDialog";
import LoadingBox from "~/components/LoadingBox";
import { useFileUpload } from "~/components/useFileUpload";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import ComparisonLevelAutocomplete from "./ComparisonLevelAutocomplete";
import CoordinateSystemAutocomplete from "./CoordinateSystemAutocomplete";
import { useFileExplorerDialog } from "./useFileExplorerDialog";

type Form = {
  id: number | null;
  mapAreaId: number | null;
  description: string;
  comparisonLevelId: number | null;
  coordinateSystemId: number | null;
  createTimestamp: Date | null;
  uploadTimestamp: Date | null;
  revisionNumber: number;
  revisionTimestamp: Date | null;
  revisionDescription: string;
  designer: string;
  fileIds: number[];
};

export type DialogAction = "view" | "edit" | "create";

type Props = {
  materialId?: number | null;
  action: DialogAction;
  onClose: () => void;
};
export const DesignMaterialDialog = ({
  materialId,
  action,
  onClose,
}: Props): JSX.Element => {
  const { t } = useTranslation("design-material");
  const { getDesignMaterial, addDesignMaterial, editDesignMaterial } =
    useDesignMaterialApi();
  const { getMapAreas } = useMapAreaApi();
  const { FileExplorerDialog, setFileDialogOpen } = useFileExplorerDialog();
  const [selectedFiles, setSelectedFiles] = React.useState<ReturnNodeDto[]>();
  const [files, setFiles] = React.useState<File[]>([]);
  const readOnly = action === "view";
  const { show } = usePopup();
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [filesChanged, setFilesChanged] = React.useState<boolean>(false);

  const formId = "map-design-material-form";

  const { data, isLoading } = useCustomQuery(
    { key: "design-material", id: materialId },
    () => getDesignMaterial(materialId!),
    {
      enabled: !!materialId,
      onSuccess: (d) => {
        setSelectedFiles(d.files ?? []);
      },
    },
  );

  const { data: mapAreas } = useCustomQuery("map-areas", () => getMapAreas(), {
    select: (d) => d.results,
  });

  const isViewing = React.useMemo(() => action === "view", [action]);
  const isCreating = React.useMemo(() => action === "create", [action]);
  const isEditing = React.useMemo(() => action === "edit", [action]);

  const { mutateAsync: createAsync } = useCustomMutation(
    (d: DesignMaterialUpdateDto) => addDesignMaterial(d),
  );

  const { mutateAsync: editAsync } = useCustomMutation(
    ({ id, data: d }: { id: number; data: DesignMaterialUpdateDto }) =>
      editDesignMaterial(d, id),
  );

  const formProps: SmartFormProps<Form>["formProps"] = React.useMemo(
    () => ({
      defaultValues: {
        comparisonLevelId: data?.comparisonLevel?.id ?? null,
        coordinateSystemId: data?.coordinateSystem?.id ?? null,
        description: data?.description ?? "",
        mapAreaId: data?.mapArea?.mapAreaId ?? null,
        designer: data?.designer ?? "",
        revisionDescription: data?.revisionDescription ?? "",
        uploadTimestamp: data?.uploadTimestamp ?? null,
        revisionTimestamp: data?.revisionTimestamp ?? null,
        createTimestamp: data?.createTimestamp ?? null,
        fileIds: data?.files?.map((x) => x.id) ?? [],
        revisionNumber: data?.revisionNumber ?? 0,
        id: data?.id ?? null,
      },
    }),
    [
      data?.comparisonLevel,
      data?.coordinateSystem,
      data?.createTimestamp,
      data?.description,
      data?.designer,
      data?.files,
      data?.id,
      data?.mapArea,
      data?.revisionDescription,
      data?.revisionNumber,
      data?.revisionTimestamp,
      data?.uploadTimestamp,
    ],
  );
  const formMethods = useSmartForm<Form>(formProps);

  React.useEffect(() => {
    formMethods.register("fileIds");
  }, [formMethods]);

  const { uploadFiles } = useFileUpload();

  const { mutateAsync: uploadFilesMutation } = useCustomMutation(
    ({ id, filesToUpload }: { id: number; filesToUpload: File[] }) =>
      uploadFiles(id, filesToUpload),
    {
      onSuccess: () => {
        onClose?.();
      },
    },
  );

  const onSubmit = React.useCallback(
    async (d: Form) => {
      if (!d.mapAreaId || !d.coordinateSystemId) return;

      const modifiedData: DesignMaterialUpdateDto = {
        mapAreaId: d.mapAreaId,
        designer: d.designer ?? undefined,
        comparisonLevelId: d.comparisonLevelId ?? undefined,
        coordinateSystemId: d.coordinateSystemId,
        createTimestamp: d.createTimestamp ?? undefined,
        description: d.description,
        fileIds: d.fileIds ?? undefined,
        revisionDescription: d.revisionDescription,
        revisionNumber: d.revisionNumber ?? undefined,
        revisionTimestamp: d.revisionTimestamp ?? undefined,
        uploadTimestamp: d.uploadTimestamp ?? undefined,
      };

      if (isCreating) {
        // Create new and wait for reply of the node id
        // Then add the possible new files to that node
        setUploading(true);
        try {
          const node = await createAsync(modifiedData);
          if (node && files.length) {
            await uploadFilesMutation({ id: node.id, filesToUpload: files });
          }
          onClose();
        } finally {
          setUploading(false);
        }
      } else if (isEditing) {
        if (!data) return;
        setUploading(true);

        Promise.all<Promise<ApiResponse[] | ReturnNodeDto>>([
          ...(formMethods.formState.isDirty
            ? [editAsync({ id: materialId!, data: modifiedData })]
            : []),
          ...(files.length
            ? [
                uploadFilesMutation(
                  { id: data.fileFolderId, filesToUpload: files },
                  {
                    // NOTE: This console log is really important,
                    // as it will prevent the mutations own onSuccess from firing
                    onSuccess: () => console.log(""),
                  },
                ),
              ]
            : []),
        ])
          .then(() => {
            onClose();
          })
          .finally(() => setUploading(false));
      }
    },
    [
      createAsync,
      data,
      editAsync,
      files,
      formMethods.formState.isDirty,
      isCreating,
      isEditing,
      materialId,
      onClose,
      uploadFilesMutation,
    ],
  );

  const getTitle = React.useCallback(() => {
    if (isCreating) return t("general:create");
    if (isEditing) return t("general:edit");
    return t("general:view");
  }, [isCreating, isEditing, t]);

  return (
    <>
      <Dialog open onClose={onClose}>
        <DialogTitle sx={{ m: 0, p: 2, pl: 3 }}>{getTitle()}</DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <LoadingBox />
          ) : (
            <Container maxWidth={false}>
              <SmartForm<Form>
                onSubmit={onSubmit}
                formId={formId}
                gridProps={{ justifyContent: "center" }}
                methods={formMethods}
              >
                <SmartAutoComplete<Form, number>
                  name="mapAreaId"
                  fullWidth
                  disabled={isViewing}
                  gridProps={{ xs: 12 }}
                  options={mapAreas?.map((a) => a.mapAreaId) ?? []}
                  getOptionLabel={(o) => {
                    const opt = mapAreas?.find((a) => a.mapAreaId === o);
                    if (opt) return opt.name;
                    return t("general:unknown");
                  }}
                  controllerProps={{
                    rules: {
                      required: { value: true, message: t("form:required") },
                    },
                  }}
                  label={t("map-area")}
                />
                <SmartTextField<Form>
                  name="description"
                  label={t("description")}
                  gridProps={{ xs: 12 }}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <ComparisonLevelAutocomplete<Form>
                  name="comparisonLevelId"
                  readOnly={readOnly}
                  gridProps={{ xs: 8, lg: 4 }}
                  formMethods={formMethods}
                />
                <SmartTextField<Form>
                  name="designer"
                  label={t("designer")}
                  gridProps={{ xs: 8, lg: 4 }}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <CoordinateSystemAutocomplete<Form>
                  name="coordinateSystemId"
                  readOnly={readOnly}
                  gridProps={{ xs: 8, lg: 4 }}
                  formMethods={formMethods}
                  required
                />
                <SmartDateTimePicker<Form>
                  name="createTimestamp"
                  label={t("create-timestamp")}
                  gridProps={{ xs: 8, lg: 6 }}
                  textfieldProps={{ fullWidth: true }}
                  clearable
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <SmartDateTimePicker<Form>
                  name="uploadTimestamp"
                  label={t("upload-timestamp")}
                  gridProps={{ xs: 8, lg: 6 }}
                  textfieldProps={{ fullWidth: true }}
                  clearable
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <SmartTextField<Form>
                  name="revisionDescription"
                  label={t("revision-description")}
                  gridProps={{ xs: 12 }}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <SmartTextField<Form>
                  name="revisionNumber"
                  label={t("revision-number")}
                  gridProps={{ xs: 8, lg: 6 }}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
                <SmartDateTimePicker<Form>
                  name="revisionTimestamp"
                  label={t("revision-timestamp")}
                  gridProps={{ xs: 8, lg: 6 }}
                  textfieldProps={{ fullWidth: true }}
                  clearable
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </SmartForm>
              <Grid
                container
                wrap="nowrap"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ marginTop: 3 }}
                direction="column"
              >
                {!readOnly && (
                  <Grid item>
                    <ButtonGroup variant="contained">
                      <Button
                        onClick={() => setFileDialogOpen(true)}
                        variant="contained"
                        color="secondary"
                        disabled={readOnly}
                      >
                        {t("add-existing-files")}
                      </Button>
                      <Button
                        onClick={() =>
                          show(
                            ({ close }) => (
                              <FileImportDialog
                                onClose={close}
                                onSubmit={(f) => {
                                  setFiles((off) => [...off, ...f]);
                                  setFilesChanged(true);
                                }}
                              />
                            ),
                            {
                              dialogProps: { maxWidth: "lg", fullWidth: true },
                            },
                          )
                        }
                        variant="contained"
                        color="secondary"
                        disabled={readOnly}
                      >
                        {t("add-new-files")}
                      </Button>
                    </ButtonGroup>
                  </Grid>
                )}
                {selectedFiles?.length || files.length ? (
                  <Grid item xs={10} lg={6} sx={{ marginTop: 1 }}>
                    <Typography align="center" variant="h6">
                      {`${t("selected-files")}:`}
                    </Typography>
                    <List sx={{ maxHeight: 400, overflow: "auto" }}>
                      {selectedFiles?.map((s) => (
                        <ListItem key={s.id}>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                noWrap
                                title={s.fullName}
                              >
                                {s.fullName}
                              </Typography>
                            }
                            disableTypography
                            style={{ marginRight: 5 }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              disabled={readOnly}
                              onClick={() => {
                                const newFiles = selectedFiles.filter(
                                  (x) => x.id !== s.id,
                                );
                                formMethods.setValue(
                                  "fileIds",
                                  newFiles.map((x) => x.id),
                                  {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                  },
                                );
                                setSelectedFiles(newFiles);
                              }}
                              size="large"
                            >
                              <CloseIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                      {files?.map((s) => (
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        <ListItem key={s.name + s.size}>
                          <ListItemText
                            primary={
                              <Typography variant="body1" noWrap title={s.name}>
                                {s.name}
                              </Typography>
                            }
                            disableTypography
                            style={{ marginRight: 5 }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              disabled={readOnly}
                              onClick={() => {
                                setFiles(files.filter((f) => f !== s));
                                setFilesChanged(true);
                              }}
                              size="large"
                            >
                              <CloseIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                ) : null}
              </Grid>
            </Container>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>
            {t(isViewing ? "general:close" : "general:cancel")}
          </Button>
          <If condition={isEditing || isCreating}>
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
              disabled={
                (!filesChanged && !formMethods.formState.isDirty) || uploading
              }
              startIcon={uploading ? <CircularProgress size={18} /> : null}
            >
              {t("general:save")}
            </Button>
          </If>
        </DialogActions>
      </Dialog>
      <FileExplorerDialog>
        <FileExplorer
          onClose={() => setFileDialogOpen(false)}
          onFileSelected={(sfiles) => {
            setSelectedFiles((off) => {
              const f = [...(off ?? []), ...sfiles];
              formMethods.setValue("fileIds", f.map((x) => x.id) ?? null, {
                shouldDirty: true,
                shouldTouch: true,
              });
              return f;
            });
            setFileDialogOpen(false);
          }}
        />
      </FileExplorerDialog>
    </>
  );
};
